/**
 * Who is signed in, and how to stop being signed in.
 *
 * WHY THIS IS A MENU AND NOT TWO THINGS IN THE BAR
 *
 * The address and the sign-out control sat in the header as plain text and a
 * link. Neither is used often -- one person uses this application and they know
 * who they are -- and between them they took the width of two nav items on a
 * bar that has seven. An icon that opens a panel gives that width back and puts
 * the identity where every other application puts it.
 *
 * IT IS DELIBERATELY THIN. The operator asked for the icon and the panel, with
 * more to come. The first thing to come was Settings (2026-09-13): closing the
 * books is set a few times a year, so it lives here rather than in the bar of
 * screens used every day.
 *
 * IT IS ALSO WHERE A LOGIN CHANGES BUSINESS (issue #50). With more than one,
 * the business the screen is showing is named beside the icon -- entering a
 * bill into the wrong books is the mistake two businesses invite, so which
 * books these are is never behind a click -- and the menu lists the others.
 * With one, nothing changes on screen. Switching reloads the app from the top,
 * so no screen carries one business's figures into the other.
 *
 * THE DISMISSAL BEHAVIOUR IS COPIED, NOT REINVENTED, from `NewEntryMenu`:
 * `mousedown` rather than `click`, or dismissing the panel swallows the first
 * click outside it and reaching anything takes two.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '../api.js';
import { useAuth } from '../auth/AuthProvider.js';
import { reloadTo } from '../lib/reload.js';

export function ProfileMenu() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  const businesses = session?.businesses ?? [];
  const several = businesses.length > 1;
  const others = businesses.filter((business) => business.tenantId !== session?.tenantId);

  async function switchTo(tenantId: string) {
    setSwitching(tenantId);
    setSwitchError(null);
    try {
      await api.switchBusiness(tenantId);
      // From the top, so nothing read for the old business is still on screen.
      reloadTo('/');
    } catch (caught) {
      setSwitching(null);
      setSwitchError(
        caught instanceof ApiError && caught.message !== caught.code
          ? caught.message
          : 'Could not switch business. Try again.',
      );
    }
  }

  useEffect(() => {
    if (!open) return;

    function onPointer(event: MouseEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /*
   * The initial, not a picture. There are no avatars in this application and
   * inventing one would be a second identity to keep in step with the real
   * one. An empty session still gets a circle rather than a blank gap.
   */
  const initial = session?.email?.trim().charAt(0).toUpperCase() ?? '?';

  return (
    <div className="profile" ref={root}>
      {several && session?.businessName && (
        <span className="profile-business" title={session.businessName}>
          {session.businessName}
        </span>
      )}
      <button
        type="button"
        className="profile-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        // The address is the accessible name. An icon labelled "Account" tells
        // a screen reader less than the header did before it became an icon.
        aria-label={
          session?.email
            ? `Account: ${session.email}${several && session.businessName ? `, in ${session.businessName}` : ''}`
            : 'Account'
        }
        onClick={() => setOpen((was) => !was)}
      >
        <span aria-hidden="true">{initial}</span>
      </button>
      {open && (
        <div className="profile-menu" role="menu">
          {/* Stated, not hidden behind the icon: the whole reason the address
              was in the bar was to make "which account is this" answerable. */}
          <p className="profile-email">{session?.email ?? 'Not signed in'}</p>
          {several && (
            <>
              <p className="profile-current">
                In <strong>{session?.businessName}</strong>
              </p>
              {others.map((business) => (
                <button
                  key={business.tenantId}
                  type="button"
                  role="menuitem"
                  className="link"
                  disabled={switching !== null}
                  onClick={() => { void switchTo(business.tenantId); }}
                >
                  {switching === business.tenantId ? 'Switching…' : `Switch To ${business.name}`}
                </button>
              ))}
              {switchError && <p role="alert" className="error">{switchError}</p>}
            </>
          )}
          <button
            type="button"
            role="menuitem"
            className="link"
            onClick={() => {
              setOpen(false);
              navigate('/settings');
            }}
          >
            Settings
          </button>
          {/* Moved from the top bar, 2026-09-13: a status page visited on
              purpose, while the banner covers the case that needs attention. */}
          <button
            type="button"
            role="menuitem"
            className="link"
            onClick={() => {
              setOpen(false);
              navigate('/sync');
            }}
          >
            Sync
          </button>
          <button
            type="button"
            role="menuitem"
            className="link"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
